/** Calendar month boundaries for schedule pricing use this TZ (matches discount copy on Schedule). */
export const SCHEDULE_PRICING_TIMEZONE = "Asia/Jerusalem";

function getYmdInTimeZone(dateInput, timeZone) {
  const d =
    typeof dateInput === "string" || typeof dateInput === "number"
      ? new Date(dateInput)
      : dateInput;
  if (!d || Number.isNaN(d.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(d);
  const y = Number(parts.find((p) => p.type === "year")?.value);
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  return { y, m, day };
}

/**
 * Group classes that a studio-style limitless pass would apply to (excludes individual & online-only).
 */
export function isClassCountableForLimitlessPricing(classObj) {
  if (!classObj?.start) return false;
  if (classObj.isCancelled === true) return false;
  if (classObj.status === "cancelled") return false;
  if (classObj.isIndividual === true) return false;
  if (classObj.type === "online") return false;
  return true;
}

/**
 * How many such classes fall in the calendar month of `now` in {@link SCHEDULE_PRICING_TIMEZONE}.
 */
export function countScheduleClassesInCalendarMonth(
  classes,
  now = new Date(),
  timeZone = SCHEDULE_PRICING_TIMEZONE
) {
  if (!Array.isArray(classes) || classes.length === 0) return 0;
  const ref = getYmdInTimeZone(now, timeZone);
  if (!ref) return 0;
  let n = 0;
  for (const c of classes) {
    if (!isClassCountableForLimitlessPricing(c)) continue;
    const cls = getYmdInTimeZone(c.start, timeZone);
    if (cls && cls.y === ref.y && cls.m === ref.m) n += 1;
  }
  return n;
}

/** Sun → Sat (getDay: 0 = Sunday … 6 = Saturday) */
export const DAY_ORDER_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export function filterUpcomingClasses(classes, now = new Date()) {
  return classes.filter((c) => new Date(c.start) > now);
}

/** Hour (0–23) and weekday (0 = Sunday) of a class in studio time, independent of the viewer's timezone. */
function getHourInTimeZone(dateInput, timeZone = SCHEDULE_PRICING_TIMEZONE) {
  const hour = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(new Date(dateInput));
  return Number(hour);
}

function getWeekdayInTimeZone(dateInput, timeZone = SCHEDULE_PRICING_TIMEZONE) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(new Date(dateInput)).toLowerCase();
  return DAY_ORDER_KEYS.indexOf(name);
}

export function filterMorningClasses(upcomingClasses) {
  return upcomingClasses.filter((c) => getHourInTimeZone(c.start) < 12);
}

export function filterEveningClasses(upcomingClasses) {
  return upcomingClasses.filter((c) => getHourInTimeZone(c.start) >= 17);
}

export function getClassesByDescription(upcomingClasses, description) {
  return upcomingClasses.filter((c) => c.description === description);
}

export function getUniqueClassesByDescription(classList) {
  const seen = new Set();
  return classList.filter((classObj) => {
    const description = classObj.description || classObj.title;
    if (!description || seen.has(description)) return false;
    seen.add(description);
    return true;
  });
}

export function translateLocationName(locationName, scheduleDetails) {
  const locationMap = {
    'Park Leumi': scheduleDetails.parkLeumi,
    Studio: scheduleDetails.studio,
    'Ramat Gan Rooftop': scheduleDetails.ramatGanRooftop,
  };
  return locationMap[locationName] || locationName;
}

export function getDaysOfWeekLabels(classObj, upcomingClasses, daysTrans) {
  const classInstances = getClassesByDescription(upcomingClasses, classObj.description);
  const dayIndices = new Set();
  classInstances.forEach((c) => {
    dayIndices.add(getWeekdayInTimeZone(c.start));
  });
  return DAY_ORDER_KEYS.map((_, i) => i)
    .filter((i) => dayIndices.has(i))
    .map((i) => daysTrans[DAY_ORDER_KEYS[i]] || DAY_ORDER_KEYS[i]);
}

export function getMostCommonTime(classObj, upcomingClasses) {
  const classInstances = getClassesByDescription(upcomingClasses, classObj.description);
  const timeCount = {};
  classInstances.forEach((c) => {
    const time = new Date(c.start).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jerusalem',
    });
    timeCount[time] = (timeCount[time] || 0) + 1;
  });
  if (Object.keys(timeCount).length === 0) return '';
  return Object.keys(timeCount).reduce((a, b) =>
    timeCount[a] > timeCount[b] ? a : b
  );
}

export function getMostCommonLocation(classObj, upcomingClasses) {
  const classInstances = getClassesByDescription(upcomingClasses, classObj.description);
  const locationCount = {};
  classInstances.forEach((c) => {
    if (c.locationName) {
      const key = `${c.locationName}|${c.locationUrl || ''}`;
      locationCount[key] = (locationCount[key] || 0) + 1;
    }
  });
  if (Object.keys(locationCount).length === 0) return null;
  const mostCommon = Object.keys(locationCount).reduce((a, b) =>
    locationCount[a] > locationCount[b] ? a : b
  );
  const [locationName, locationUrl] = mostCommon.split('|');
  return { locationName, locationUrl };
}

export function getMostCommonDuration(classObj, upcomingClasses) {
  const classInstances = getClassesByDescription(upcomingClasses, classObj.description);
  const durationCount = {};
  classInstances.forEach((c) => {
    durationCount[c.durationMinutes] = (durationCount[c.durationMinutes] || 0) + 1;
  });
  if (Object.keys(durationCount).length === 0) return classObj.durationMinutes;
  return Object.keys(durationCount).reduce((a, b) =>
    durationCount[a] > durationCount[b] ? a : b
  );
}

/**
 * Shared shape for schedule UIs: morning / evening unique slots with display fields.
 */
export function prepareLiveLessonsSchedule(classes, scheduleDetails, daysTrans, now = new Date()) {
  const upcoming = filterUpcomingClasses(classes, now);
  const morningClasses = filterMorningClasses(upcoming);
  const eveningClasses = filterEveningClasses(upcoming);

  // Days / time / duration / location come only from the same period's classes, so a class
  // that runs both mornings and evenings shows its real times in each column.
  const toSlot = (periodClasses) => (classObj) => {
    const loc = getMostCommonLocation(classObj, periodClasses);
    return {
      key: classObj.description || classObj.title,
      title: classObj.title || classObj.description,
      daysLine: getDaysOfWeekLabels(classObj, periodClasses, daysTrans).join(', '),
      time: getMostCommonTime(classObj, periodClasses),
      duration: getMostCommonDuration(classObj, periodClasses),
      location:
        loc?.locationName != null
          ? {
              name: loc.locationName,
              url: loc.locationUrl,
              label: translateLocationName(loc.locationName, scheduleDetails),
            }
          : null,
    };
  };

  return {
    upcoming,
    morning: getUniqueClassesByDescription(morningClasses).map(toSlot(morningClasses)),
    evening: getUniqueClassesByDescription(eveningClasses).map(toSlot(eveningClasses)),
  };
}

/**
 * @returns {{ id: string, daysLine: string, time: string, durationMinutes: number | null, location: { name?: string, label: string, url: string } | null }[]}
 */
export function getCompactScheduleSummarySegments(
  classes,
  scheduleDetails,
  daysTrans,
  now = new Date()
) {
  const { morning, evening } = prepareLiveLessonsSchedule(
    classes,
    scheduleDetails,
    daysTrans,
    now
  );
  const slotsOrdered = [...morning, ...evening];
  const seen = new Set();
  const segments = [];
  for (const slot of slotsOrdered) {
    const head = [slot.daysLine, slot.time].filter(Boolean).join(' ');
    if (!head) continue;
    const locSuffix = slot.location?.label ? ` (${slot.location.label})` : '';
    const piece = `${head}${locSuffix}`;
    if (seen.has(piece)) continue;
    seen.add(piece);
    const url = slot.location?.url ? String(slot.location.url).trim() : '';
    const loc =
      slot.location?.label != null
        ? {
            name: slot.location.name,
            label: slot.location.label,
            url,
          }
        : null;
    const dur = slot.duration;
    const durationMinutes =
      dur != null && dur !== '' && !Number.isNaN(Number(dur))
        ? Number(dur)
        : null;
    segments.push({
      id: piece,
      daysLine: slot.daysLine || '',
      time: slot.time || '',
      durationMinutes,
      location: loc,
    });
  }
  return segments;
}

export function formatCompactScheduleSummary(
  classes,
  scheduleDetails,
  daysTrans,
  now = new Date()
) {
  return getCompactScheduleSummarySegments(
    classes,
    scheduleDetails,
    daysTrans,
    now
  )
    .map((seg) => {
      const loc = seg.location?.label ? ` (${seg.location.label})` : '';
      const head = [seg.daysLine, seg.time].filter(Boolean).join(' ');
      return `${head}${loc}`;
    })
    .join('. ');
}
